import type { Cart, Site, SyncRequest, SyncResponse } from '@chariot/contract';

/** Same-origin in production; the dev server proxies /api and /auth to the API. */
async function call<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      ...init,
      credentials: 'include',
      headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    });
  } catch (cause) {
    // fetch rejects when the server cannot be reached at all. That is not a fault to report
    // to an operator — it is the normal condition this whole application is built for.
    throw new NetworkError(cause);
  }
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new ApiError(res.status, detail || res.statusText);
  }
  return (await res.json()) as T;
}

/** The server could not be reached. Distinct from a server that answered with an error. */
export class NetworkError extends Error {
  constructor(readonly cause: unknown) {
    super('server unreachable');
    this.name = 'NetworkError';
  }
}

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
  get unauthenticated() {
    return this.status === 401;
  }
  get forbidden() {
    return this.status === 403;
  }
}

export interface Identity {
  user_id: string;
  display_name: string;
  roles: ('operator' | 'admin')[];
}

export const api = {
  me: () => call<Identity>('/auth/me'),
  devLogin: (name: string, roles?: Identity['roles']) =>
    call<Identity>('/auth/login', { method: 'POST', body: JSON.stringify({ name, roles }) }),
  logout: () => call<{ ok: true }>('/auth/logout', { method: 'POST' }),

  sync: (body: SyncRequest) =>
    call<SyncResponse>('/api/sync', { method: 'POST', body: JSON.stringify(body) }),

  createSite: (name: string) =>
    call<Site>('/api/sites', { method: 'POST', body: JSON.stringify({ name }) }),
  createCart: (name: string) =>
    call<Cart>('/api/carts', { method: 'POST', body: JSON.stringify({ name }) }),
  createCartsBulk: (count: number, name_prefix: string) =>
    call<Cart[]>('/api/carts/bulk', { method: 'POST', body: JSON.stringify({ count, name_prefix }) }),
  updateCart: (code: string, patch: { name?: string; status?: Cart['status'] }) =>
    call<Cart>(`/api/carts/${code}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  updateSite: (code: string, patch: { name?: string; status?: Site['status'] }) =>
    call<Site>(`/api/sites/${code}`, { method: 'PATCH', body: JSON.stringify(patch) }),
};
